import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import StatCard from '../components/StatCard.jsx';
import ChartCard from '../components/ChartCard.jsx';
import TimeSeriesLineChart from '../components/TimeSeriesLineChart.jsx';
import DecimalValueFormat from '../components/DecimalValueFormat.jsx';

import { getSiteData, getSiteState, getSiteDataSourcesForTypes, getSiteLastData } from '../helpers/SiteData.js';
import { SITE_TYPE_DAM, STATUS_NO_CHANGE, STATUS_NO_RECENT_DATA, STATUS_NO_DATA } from '../helpers/SiteDataConst.js';
import ChartStatCard from '../components/ChartStatCard.jsx';
import AboveBelowFormat from '../components/AboveBelowFormat.jsx';
import { determineDischarge, determineStorage } from '../helpers/DamHelper.js';

const DAM_CHANGE_HOURS = 12;

function RiverDamBlock({site, allData}) {
  const {t} = useTranslation();
  const data = getSiteData(allData, site.id, SITE_TYPE_DAM);
  const latestData = getSiteLastData(allData, site.id, SITE_TYPE_DAM);
  const state = getSiteState(allData, site.id, SITE_TYPE_DAM);
  const dataSource = getSiteDataSourcesForTypes(site, [SITE_TYPE_DAM]);
  const damLevelCapacityStat = determineDavolumeevelCapacityStat(t, site, latestData);
  const { gateState, dischargeRate } = determineDischarge(t, site, latestData?.value, state);
  const storageStats = determineStorage(t, site, latestData?.value);

  return (
    <>
      <h2 id={site.id}>{site.name} Conditions</h2>
      <Row>
        <Col md={12} lg={8} className="mb-4">
          <ChartStatCard
            title="Dam Level"
            noData={latestData?.value === undefined}
            stat={latestData?.value ? DecimalValueFormat({value: latestData?.value}) : undefined}
            secondStat={damLevelCapacityStat}
            time={data?.[0]?.timestamp}
            unit={t(`units.${site.properties.unit}.label`)}
            status={state?.siteState}
            dataSource={dataSource}
          >
            <TimeSeriesLineChart
              data={data != undefined ? [data] : undefined}
              dataSeries={[SITE_TYPE_DAM]}
              minDate={DateTime.now().minus({days: 7}).toJSDate()}
              maxDate={DateTime.now().toJSDate()}
              title="Ross River Dam"
              unit={t(`units.${site.properties.unit}.label`)}
              margin={{ top: 20, right: 40, bottom: 60, left: 40 }}
              markers={SiteChartMarkers(t, site, t(`units.${site.properties.unit}.label`))}
            />
          </ChartStatCard>
        </Col>
        <Col md={12} lg={4}>
          <StatCard
            className="mb-4"
            stat={storageStats?.percent ? (storageStats?.percent * 100).toFixed(2) : undefined}
            unit={'%'}
            secondStat={`${storageStats.volume} Megalitres`}
            time={latestData?.timestamp}
            title="Dam Storage"
            dataSource={dataSource}
          />
          <StatCard
            className="mb-4"
            stat={gateState ? t(`dams.gate_state.${gateState?.toLowerCase()}`) : undefined}
            secondStat={dischargeRate ? `Discharging ${dischargeRate} m³/s` : undefined}
            disclaimer="Approximation based on TCC Ross River Dam EAP"
            title="Spillway Gates"
          />
        </Col>
      </Row>
    </>
  )
}

function determineDavolumeevelCapacityStat(t, site, lastData) {
  const damLevelAboveBelow = lastData?.value - site.properties.capacity;
  let damLevelCapacityStat = '';
  if (damLevelAboveBelow > 0) {
    damLevelCapacityStat = t(`levels.above_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  } else if (damLevelAboveBelow < 0) {
    damLevelCapacityStat = t(`levels.below_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  } else {
    damLevelCapacityStat = t(`levels.equal_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  }

  return damLevelCapacityStat
}

function SiteChartMarkers(t, site= {}, unit) {
  let markers = [];

  if (site.properties?.benchmarks) {
    const chartBenchmarks = site.properties.benchmarks.filter((benchmark) => benchmark.displayOnChart);
    const benchmarkMarkers = chartBenchmarks.map((benchmark) => {
      return {
        axis: 'y',
        value: benchmark.value,
        lineStyle: {
          stroke: '#42708C',
          strokeWidth: 1,
        },
        textStyle: {
          fill: '#42708C',
          fontSize: 10,
        },
        legend: `${benchmark.name} - ${benchmark.value} ${unit}`,
        legendPosition: 'top-right',
        legendOffsetY: 8,
        legendOffsetX: 2,
      };
    });
    markers = markers.concat(benchmarkMarkers)
  }

  return markers
}

export default RiverDamBlock
